import React, { useCallback, useEffect } from 'react';
import { debounce, uniqBy } from 'lodash';
import { CustomTagsStyled } from './styled';
import { BlockStack, Collapsible, TextField, Text, Button, Icon } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import { previewSelector } from '@/redux/slice/preview.slice';
import { PageType } from '@/constants/enum';
import { useTranslation } from 'react-i18next';
import { handleChangePreview } from '@/redux/slice/preview.slice';
import { XSmallIcon } from '@shopify/polaris-icons';
import useLimitPlan from '@/hooks/useLimitPlan';

interface Props {
  errorSpecific: string;
  setErrorSpecific: React.Dispatch<React.SetStateAction<string>>;
}

const CustomTags: React.FC<Props> = ({ errorSpecific, setErrorSpecific }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['popup', 'common']);

  const previewDetail = useSelector(previewSelector);
  const { isShowUpgradeNowRulePopup } = useLimitPlan();
  const [value, setValue] = React.useState('');
  const showInPagesRef = React.useRef(previewDetail.showInPages);

  const listTagsForHelpText = previewDetail.showInPages;

  useEffect(() => {
    showInPagesRef.current = previewDetail.showInPages;
  }, [previewDetail.showInPages]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleBlur = useCallback(
    debounce(() => {
      const currentShowInPages = showInPagesRef.current;
      if (currentShowInPages.length === 0) {
        setErrorSpecific(t('specific_tags_required'));
      }
    }, 200),
    [dispatch, previewDetail.showInPages],
  );

  const handleAdd = () => {
    if (errorSpecific) {
      setErrorSpecific('');
    }

    const tags = value
      ?.split(',')
      ?.map((tag) => tag.trim())
      ?.filter((tag) => tag.length > 0);

    const newTags = tags?.map((tag) => ({ id: Math.random().toString(), title: tag }));

    const uniqueTags = uniqBy([...previewDetail.showInPages, ...newTags], 'title');

    dispatch(
      handleChangePreview({
        showInPages: uniqueTags,
      }),
    );

    setValue('');
  };

  const onRemoveTag = (id: string) => {
    const newShowInPages = previewDetail.showInPages.filter((tag) => +tag.id !== +id);
    dispatch(handleChangePreview({ showInPages: newShowInPages }));

    if (newShowInPages.length === 0) {
      setErrorSpecific(t('specific_tags_required'));
    }
  };

  return (
    <Collapsible
      id="custom_tags"
      open={previewDetail.pageTypeToShow === PageType.SpecificTags}
      transition={{ duration: '300ms' }}
    >
      <CustomTagsStyled>
        <BlockStack gap="200">
          <TextField
            disabled={isShowUpgradeNowRulePopup}
            autoComplete=""
            label={
              <Text as="span" variant="headingMd">
                {t('value')}
              </Text>
            }
            value={value}
            error={errorSpecific}
            onChange={setValue}
            requiredIndicator
            placeholder={t('enter_value')}
            onBlur={handleBlur}
            connectedRight={
              <Button size="large" onClick={handleAdd} variant="primary" disabled={value.trim().length <= 0}>
                {t('common:add')}
              </Button>
            }
            helpText={
              listTagsForHelpText?.length > 0
                ? listTagsForHelpText.map((tag) => (
                    <div className="mr-8 Polaris-Tag mb-4 break-word" key={tag.id}>
                      <h1 title={tag.title} className="Polaris-Tag__TagText">
                        {tag.title}
                      </h1>
                      <button
                        disabled={isShowUpgradeNowRulePopup}
                        className="Polaris-Tag__Button"
                        onClick={() => onRemoveTag(tag.id)}
                      >
                        <Icon source={XSmallIcon}></Icon>
                      </button>
                    </div>
                  ))
                : null
            }
          />
        </BlockStack>
      </CustomTagsStyled>
    </Collapsible>
  );
};

export default CustomTags;
